import React, { useState } from 'react';
import { EvvReconciliationService } from '../../../../services/evv/reconciliation.service';
import { OVERRIDE_REASON_MAX, OVERRIDE_REASON_MIN, errorMessage } from './evvUi';

interface EvvOverrideFormProps {
  patientId: string;
  visitId: string;
  /** Documented Pelora minutes, when known: the upper bound and the default */
  peloraMinutes?: number;
  onDone: (message: string) => void;
}

/**
 * 1.8 / BIL-06 EVV override. Rendered only for users with evv:override (Admin); the backend
 * enforces the same rule and the bounds (reason 10–500 characters, minutes 1..documented minutes).
 */
const EvvOverrideForm: React.FC<EvvOverrideFormProps> = ({ patientId, visitId, peloraMinutes, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = reason.trim();
  const minutesValue = minutes === '' ? undefined : Number(minutes);
  const minutesInvalid = minutesValue !== undefined
    && (!Number.isInteger(minutesValue) || minutesValue < 1 || (peloraMinutes !== undefined && minutesValue > peloraMinutes));
  const canSave = trimmed.length >= OVERRIDE_REASON_MIN && trimmed.length <= OVERRIDE_REASON_MAX && !minutesInvalid && !saving;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-slate-400 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50"
      >
        EVV override (Admin)
      </button>
    );
  }

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await EvvReconciliationService.overrideVisit(patientId, visitId, { reason: trimmed, evvVerifiedMinutes: minutesValue });
      onDone(`EVV override saved: ${result.evvVerifiedMinutes} minutes${result.evvOverride.claimFlag ? ' (the claim will be flagged as not HHAX-verified)' : ''}`);
      setOpen(false);
      setReason('');
      setMinutes('');
    } catch (err) {
      setError(errorMessage(err, 'Failed to apply the EVV override'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 w-full rounded-md border border-slate-300 bg-slate-50 p-3" data-testid={`override-form-${visitId}`}>
      <p className="text-xs text-slate-600">
        Overrides mark the visit verified for billing. Billed minutes can't exceed the documented visit
        {peloraMinutes !== undefined ? ` (${peloraMinutes} min)` : ''}. Every override is audit-logged.
      </p>
      <label className="mt-2 block text-xs font-medium text-slate-700" htmlFor={`override-reason-${visitId}`}>
        Reason (at least {OVERRIDE_REASON_MIN} characters)
      </label>
      <textarea
        id={`override-reason-${visitId}`}
        className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        rows={2}
        maxLength={OVERRIDE_REASON_MAX}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <p className="text-right text-xs text-slate-500">{trimmed.length}/{OVERRIDE_REASON_MAX}</p>
      <label className="mt-1 block text-xs font-medium text-slate-700" htmlFor={`override-minutes-${visitId}`}>
        Minutes to bill (optional; default {peloraMinutes !== undefined ? `${peloraMinutes}, the documented visit` : 'the documented visit'})
      </label>
      <input
        id={`override-minutes-${visitId}`}
        type="number"
        min={1}
        max={peloraMinutes}
        step={1}
        className="mt-1 w-40 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        value={minutes}
        onChange={(e) => setMinutes(e.target.value)}
      />
      {minutesInvalid && (
        <p className="mt-1 text-xs text-rose-700">
          Enter a whole number from 1{peloraMinutes !== undefined ? ` to ${peloraMinutes}` : ''}.
        </p>
      )}
      {error && <p className="mt-2 text-xs text-rose-700">{error}</p>}
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700">
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Apply override'}
        </button>
      </div>
    </div>
  );
};

export default EvvOverrideForm;
